import speakeasyIcon from "@/assets/speakeasy-icon.svg";
import { cn } from "@/lib/utils";
import { memo, useEffect, useState } from "react";

// Animated walkthrough of one governed agent session: five policy decisions
// light up in sequence on a ~22s loop, mirroring the product's decision log.
type DecisionStep = {
  label: string;
  chip: string;
  chipClassName: string;
  detail: string;
};

const DECISION_STEPS: DecisionStep[] = [
  {
    label: "Connect billing-api MCP",
    chip: "Granted",
    chipClassName: "bg-[var(--moss)] text-white",
    detail: "401 → OAuth via Okta · sarah@acme.com",
  },
  {
    label: "get_invoice INV-4821",
    chip: "Fields flagged",
    chipClassName: "bg-[var(--ember)] text-black",
    detail: "card, email flagged for the model",
  },
  {
    label: "create_refund $1,200",
    chip: "Denied",
    chipClassName: "bg-[var(--vermilion)] text-white",
    detail: "destructive · deny wins over team allow",
  },
  {
    label: "void_invoice — 3 duplicates",
    chip: "Held",
    chipClassName: "bg-[var(--blue)] text-white",
    detail: "llm-as-judge: held for human approval",
  },
  {
    label: "Post summary to #billing-ops",
    chip: "Granted",
    chipClassName: "bg-[var(--moss)] text-white",
    detail: "audit written · session cost $5.00",
  },
];

// Second marks at which each decision row activates, on a loop of LOOP_SECONDS.
// The first two seconds belong to the "Connected to Speakeasy" banner.
const STEP_STARTS = [3, 7, 11, 15, 19];
const LOOP_SECONDS = 24;
const TICK_MS = 100;
const SPEED = 1.25;
// Chip + detail fade in this long after their row activates.
const REVEAL_DELAY_SECONDS = 1.6;
// Connection banner brightens, then its checkmark appears.
const CONNECT_AT_SECONDS = 0.5;
const CONNECT_CHECK_AT_SECONDS = 1.6;
const SESSION_COSTS = ["$0.00", "$0.85", "$1.60", "$2.75", "$3.90", "$5.00"];

// The showcase pane is `hidden xl:flex`, so the ticker only runs when the
// viewport can actually see it. Must match Tailwind's `xl` breakpoint.
const SHOWCASE_VISIBLE_QUERY = "(min-width: 1280px)";

function useSessionClock(): number {
  // With reduced motion the clock stays parked past the last reveal, so the
  // card renders as a completed session (all rows revealed, progress at 100%)
  // instead of looping.
  const [elapsed, setElapsed] = useState(LOOP_SECONDS);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const visible = window.matchMedia(SHOWCASE_VISIBLE_QUERY);
    let timer: number | undefined;

    const sync = () => {
      if (visible.matches && timer === undefined) {
        setElapsed(0);
        timer = window.setInterval(() => {
          setElapsed((prev) => {
            const next = prev + (TICK_MS / 1000) * SPEED;
            return next >= LOOP_SECONDS ? 0 : next;
          });
        }, TICK_MS);
      } else if (!visible.matches && timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    sync();
    visible.addEventListener("change", sync);
    return () => {
      visible.removeEventListener("change", sync);
      if (timer !== undefined) {
        clearInterval(timer);
      }
    };
  }, []);

  return elapsed;
}

function activeStep(elapsed: number): number {
  return STEP_STARTS.reduce(
    (acc, start, index) => (elapsed >= start ? index : acc),
    -1,
  );
}

function rowOpacity(index: number, step: number): number {
  if (step < 0) return 0.35;
  if (index === step) return 1;
  return index < step ? 0.6 : 0.3;
}

function revealOpacity(index: number, elapsed: number): number {
  const start = STEP_STARTS[index] ?? 0;
  return elapsed >= start + REVEAL_DELAY_SECONDS ? 1 : 0;
}

// Memoized so the 10Hz ticker only reconciles rows at step boundaries; the
// per-tick DOM update is just the progress bar.
const DecisionRow = memo(function DecisionRow({
  step,
  opacity,
  reveal,
}: {
  step: DecisionStep;
  opacity: number;
  reveal: number;
}) {
  return (
    <div
      className="border border-[var(--edge-soft)] px-3 py-[9px] transition-opacity duration-500"
      style={{ opacity }}
    >
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-[14px]">{step.label}</span>
        <span
          className={cn(
            "auth-mono px-2.5 py-[3px] text-[10px] transition-opacity duration-400",
            step.chipClassName,
          )}
          style={{ opacity: reveal }}
        >
          {step.chip}
        </span>
      </div>
      <div
        className="auth-mono-text mt-[5px] text-[12px] text-[var(--muted-strong)] transition-opacity duration-400"
        style={{ opacity: reveal }}
      >
        {step.detail}
      </div>
    </div>
  );
});

function AgentSessionCard({ elapsed }: { elapsed: number }) {
  const currentStep = activeStep(elapsed);
  const cost = SESSION_COSTS[currentStep + 1];
  const progress = Math.min(100, (elapsed / (LOOP_SECONDS - 0.5)) * 100);

  return (
    <div className="w-[540px] overflow-hidden border border-[var(--edge)] bg-[var(--card)]">
      <div className="flex h-11 items-center justify-between border-b border-[var(--edge-soft)] px-5">
        <span className="auth-mono flex items-center gap-2.5 text-[12px]">
          <i className="auth-live-dot h-[7px] w-[7px] rounded-full bg-[var(--moss)]" />
          Agent session
        </span>
        <span className="auth-mono text-[12px] text-[var(--muted)]">Live</span>
      </div>

      <div
        className="mx-5 mt-3 border border-[var(--edge-soft)] bg-[var(--surface)] px-3.5 py-[9px] transition-opacity duration-500"
        style={{ opacity: elapsed >= CONNECT_AT_SECONDS ? 1 : 0.35 }}
      >
        <div className="flex items-center gap-2.5">
          <img src={speakeasyIcon} alt="" className="h-4 w-4" />
          <span className="text-[14px]">Connected to Speakeasy</span>
          <span
            className="text-[13px] text-[var(--moss)] transition-opacity duration-400"
            style={{ opacity: elapsed >= CONNECT_CHECK_AT_SECONDS ? 1 : 0 }}
          >
            ✓
          </span>
        </div>
        <div className="auth-mono-text mt-1 text-[12px] text-[var(--muted-strong)]">
          12 MCP servers · 84 tools · 6 skills · session governed
        </div>
      </div>

      <div className="flex gap-3 px-5 pt-4 pb-1.5">
        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--navy)] text-[11px] text-white">
          S
        </span>
        <p className="mt-0.5 text-[15px] leading-normal tracking-[0.0025em]">
          Handle the refund request on order #4821 — verify the invoice and
          process it if valid.
        </p>
      </div>

      <div className="flex flex-col gap-[7px] px-5 pt-3 pb-4">
        {DECISION_STEPS.map((step, index) => (
          <DecisionRow
            key={step.label}
            step={step}
            opacity={rowOpacity(index, currentStep)}
            reveal={revealOpacity(index, elapsed)}
          />
        ))}
      </div>

      <div className="auth-mono-text flex items-center justify-between px-5 pb-3.5 text-[11px] tracking-[0.04em] text-[var(--muted)]">
        <span>task: refund-request-4821</span>
        <span className="text-[13px] text-[var(--muted-strong)]">
          session cost {cost}
        </span>
      </div>

      <div className="h-[3px] bg-[var(--edge-soft)]">
        <div
          className="h-full bg-[var(--cta)] transition-[width] duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

const SOCIAL_LINKS = [
  { label: "X", href: "https://x.com/speakeasydev" },
  { label: "GitHub", href: "https://github.com/speakeasy-api/gram" },
  { label: "Docs", href: "https://www.speakeasy.com/docs/mcp" },
];

export function AgentSessionShowcase(): JSX.Element {
  const elapsed = useSessionClock();

  return (
    <div className="auth-showcase relative hidden flex-col items-center justify-center p-12 xl:flex">
      {/* The stage zooms down on short viewports (see BRAND_STYLES) so the
          fixed-size card never clips below the fold. */}
      <div className="auth-showcase-stage flex flex-col items-center gap-5">
        <h2 className="w-[540px] [font-family:var(--f-display)] text-[51px] leading-[1.1] font-thin tracking-[-0.04em]">
          Every agent action, governed.
        </h2>

        <div aria-hidden="true">
          <AgentSessionCard elapsed={elapsed} />
        </div>
      </div>

      <div className="absolute right-0 bottom-5 left-0 flex justify-center gap-7">
        {SOCIAL_LINKS.map((link) => (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="auth-mono text-[12px] tracking-[0.06em] text-[var(--muted)] transition-colors hover:text-black"
          >
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
}
